import { cn } from "@/common/helpers/utils";
import type { DetailTab } from "@/components/detail-page/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDownIcon } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

interface OverflowTabsMenuProps {
  sections: DetailTab[];
  activeTab: string | undefined;
  onSelectTab: (value: string) => void;
}

export function OverflowTabsMenu({ sections, activeTab, onSelectTab }: OverflowTabsMenuProps): ReactElement {
  const { t } = useTranslation("common");
  const activeSection = sections.find((section) => section.id === activeTab);
  const isActive = activeSection !== undefined;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-slot="tabs-trigger"
          data-state={isActive ? "active" : "inactive"}
          className={cn(
            "inline-flex cursor-pointer items-center gap-1 px-3 py-2 font-medium whitespace-nowrap",
            "focus-visible:ring-ring transition-colors duration-200 focus-visible:ring-1 focus-visible:outline-none",
            isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground",
          )}
        >
          <span className="max-w-40 truncate">{activeSection?.label ?? t("common:More")}</span>
          <ChevronDownIcon className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {sections.map((section) => (
          <DropdownMenuItem
            key={section.id}
            className={cn(section.id === activeTab && "bg-muted")}
            onClick={() => onSelectTab(section.id)}
          >
            {section.icon && <section.icon />}
            {section.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
